import { useState } from "react";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../lib/auth";
import {
  BookOpen, Users, LayoutDashboard, CalendarDays, TrendingUp, BedDouble,
  Settings as SettingsIcon, FileText, Lightbulb, AlertTriangle, Code2,
  LogIn, Image as ImageIcon, MessageCircle, Link as LinkIcon, Webhook,
  DollarSign, Download, Printer, ChevronRight,
} from "lucide-react";

const SECTIONS = [
  { id: "login", label: "1. Login & Role", icon: LogIn },
  { id: "dashboard", label: "2. Dashboard", icon: LayoutDashboard },
  { id: "bookings", label: "3. Bookings", icon: BookOpen },
  { id: "calendar", label: "4. Calendar", icon: CalendarDays },
  { id: "reports", label: "5. Reports", icon: TrendingUp },
  { id: "rooms", label: "6. Kamar & Foto", icon: BedDouble },
  { id: "settings", label: "7. Settings", icon: SettingsIcon },
  { id: "invoice", label: "8. Invoice/Kwitansi", icon: FileText },
  { id: "tips", label: "9. Tips & Best Practices", icon: Lightbulb },
  { id: "troubleshooting", label: "10. Troubleshooting", icon: AlertTriangle },
  { id: "api", label: "11. API (Developer)", icon: Code2 },
];

export default function Help() {
  const { isOwner } = useAuth();
  const [active, setActive] = useState("login");

  const go = (id) => {
    setActive(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <PageHeader
        title="Panduan Penggunaan"
        subtitle="Dokumentasi lengkap semua fitur PondokRaja — Booking Manager untuk Meos Ambower Homestay."
      />

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        {/* Sticky TOC */}
        <aside className="lg:sticky lg:top-8 self-start flex flex-col gap-1 bg-white border border-[#EBE6DE] rounded-2xl p-3 shadow-sm">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#586D61] font-bold px-3 py-2">
            Daftar Isi
          </div>
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              data-testid={`help-nav-${s.id}`}
              onClick={() => go(s.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                active === s.id
                  ? "bg-[#D16B4D]/10 text-[#D16B4D] font-medium"
                  : "text-[#586D61] hover:bg-[#FDFBF7]"
              }`}
            >
              <s.icon className="h-4 w-4 flex-shrink-0" strokeWidth={1.6} />
              <span className="truncate">{s.label}</span>
            </button>
          ))}
        </aside>

        {/* Content */}
        <article className="flex flex-col gap-8 max-w-3xl">

          <Section id="login" icon={<LogIn />} title="1. Login & Role">
            <p>Buka URL aplikasi, masuk lewat halaman <Code>/login</Code>.</p>

            <Heading>Dua role tersedia</Heading>
            <div className="rounded-xl border border-[#EBE6DE] overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FDFBF7]">
                  <tr>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#586D61]">Role</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#586D61]">Bisa</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#586D61]">Tidak Bisa</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-[#EBE6DE]">
                    <td className="px-4 py-3 font-bold text-[#D16B4D]">Owner</td>
                    <td className="px-4 py-3">Semua fitur</td>
                    <td className="px-4 py-3 text-[#586D61]">—</td>
                  </tr>
                  <tr className="border-t border-[#EBE6DE]">
                    <td className="px-4 py-3 font-bold text-[#2C7A51]">Staff</td>
                    <td className="px-4 py-3">Dashboard, Bookings (tambah/edit), Calendar, Rooms (lihat + foto)</td>
                    <td className="px-4 py-3 text-[#586D61]">Settings, Reports, hapus, upload foto, user management, season rules</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <Heading>Demo credentials</Heading>
            <ul>
              <li><strong>Owner</strong>: <Code>owner@meosambower.com</Code> / <Code>MeosAmbower2026!</Code></li>
              <li><strong>Staff</strong>: <Code>staff@meosambower.com</Code> / <Code>StaffDemo2026!</Code></li>
            </ul>
            <InfoBox>Ganti password owner lewat <Code>OWNER_PASSWORD</Code> di <Code>/app/backend/.env</Code> untuk production. Restart backend supaya hash di-update.</InfoBox>
          </Section>

          <Section id="dashboard" icon={<LayoutDashboard />} title="2. Dashboard — Ringkasan Performa Bulanan">
            <p>URL: <Code>/</Code></p>
            <p>Menampilkan metrik penting untuk bulan berjalan. Gunakan dropdown bulan/tahun di kanan atas untuk melihat periode lain.</p>

            <Heading>5 KPI Cards</Heading>
            <ul>
              <li><strong>Total Booking</strong> — jumlah booking yang overlap dengan bulan dipilih (exclude Cancelled)</li>
              <li><strong>Total Tamu</strong> — akumulasi <Code>guests</Code></li>
              <li><strong>Pendapatan</strong> — sum <Code>total_price</Code> dari booking <em>Paid + Confirmed</em> yang check-in di bulan tsb</li>
              <li><strong>Top Platform</strong> — platform paling dominan bulan ini</li>
              <li><strong>Occupancy Rate</strong> — (malam terisi ÷ (total kamar × hari bulan)) × 100%</li>
            </ul>

            <Heading>Widget tambahan</Heading>
            <ul>
              <li>Bar chart <em>Booking per Platform</em> — channel mix visual</li>
              <li>List 5 booking terbaru — klik "Lihat Semua" untuk ke halaman Bookings</li>
            </ul>
          </Section>

          <Section id="bookings" icon={<BookOpen />} title="3. Bookings — Kelola Reservasi">
            <p>URL: <Code>/bookings</Code></p>

            <Heading>Menambah Booking Baru</Heading>
            <ol>
              <li>Klik <Btn>+ Tambah Booking</Btn> (pojok kanan atas)</li>
              <li>Isi Nama Tamu, Platform, Kamar, Check-in/out. Malam dihitung otomatis.</li>
              <li>Isi <strong>Nomor WhatsApp</strong> (opsional) — wajib jika ingin tombol <MessageCircle className="h-4 w-4 inline text-[#25D366]" /> konfirmasi WA muncul di row.</li>
              <li>Klik tombol <Badge color="#D16B4D">Auto Rp X</Badge> untuk auto-hitung dari <Code>base_rate × multiplier</Code></li>
              <li>Pilih Status (Pending / Confirmed / Paid / Cancelled)</li>
              <li>Klik <Btn>Buat Booking</Btn></li>
            </ol>

            <Heading>Deteksi Konflik (anti double-booking)</Heading>
            <p>Kalau tanggal + kamar yang sama sudah terisi, sistem akan <strong>menolak</strong> dengan dialog warning berisi info booking existing. Anda bisa:</p>
            <ul>
              <li><Btn>Batal</Btn> — ubah tanggal/kamar</li>
              <li><Btn>Tetap Simpan</Btn> — override manual jika yakin</li>
            </ul>

            <Heading>Aksi per Row</Heading>
            <table className="w-full rounded-xl overflow-hidden border border-[#EBE6DE] text-sm">
              <thead className="bg-[#FDFBF7]">
                <tr><th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-[#586D61]">Icon</th><th className="text-left px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-[#586D61]">Fungsi</th></tr>
              </thead>
              <tbody>
                <tr className="border-t border-[#EBE6DE]"><td className="px-4 py-2"><FileText className="h-4 w-4 text-[#D16B4D]" /></td><td className="px-4 py-2">Buka invoice di tab baru (PDF-ready)</td></tr>
                <tr className="border-t border-[#EBE6DE]"><td className="px-4 py-2"><MessageCircle className="h-4 w-4 text-[#25D366]" /></td><td className="px-4 py-2">Kirim WhatsApp konfirmasi (pakai nomor tamu)</td></tr>
                <tr className="border-t border-[#EBE6DE]"><td className="px-4 py-2">✏️</td><td className="px-4 py-2">Edit booking</td></tr>
                <tr className="border-t border-[#EBE6DE]"><td className="px-4 py-2">🗑</td><td className="px-4 py-2">Hapus (owner only)</td></tr>
              </tbody>
            </table>

            <Heading>Export CSV & Import iCal</Heading>
            <ul>
              <li><Btn><Download className="h-3.5 w-3.5 inline mr-1" />Export CSV</Btn> — download semua booking</li>
              <li><Btn><LinkIcon className="h-3.5 w-3.5 inline mr-1" />Import iCal</Btn> — paste URL iCal dari Booking.com/Airbnb → pilih kamar + platform → import</li>
            </ul>
            <InfoBox>
              <strong>Cara dapat URL iCal:</strong><br />
              • <strong>Airbnb</strong>: Host → Listings → Calendar → ⚙️ → Export Calendar → copy URL<br />
              • <strong>Booking.com</strong>: Extranet → Calendar → Sync Calendars → Export
            </InfoBox>
          </Section>

          <Section id="calendar" icon={<CalendarDays />} title="4. Calendar — Grid Ketersediaan">
            <p>URL: <Code>/calendar</Code></p>
            <ul>
              <li>Sumbu Y: 6 bungalow</li>
              <li>Sumbu X: tanggal 1 sampai akhir bulan</li>
              <li><span className="inline-block h-3 w-3 rounded-sm bg-[#2C7A51] align-middle" /> <strong>Hijau</strong> = kosong</li>
              <li><span className="inline-block h-3 w-3 rounded-sm bg-[#D14D4D] align-middle" /> <strong>Merah</strong> = terisi</li>
              <li>Ring oranye = hari ini</li>
              <li>Hover cell → tooltip nama tamu + platform</li>
            </ul>
          </Section>

          <Section id="reports" icon={<TrendingUp />} title="5. Reports — Laporan Keuangan" ownerNote>
            <p>URL: <Code>/reports</Code> (owner only)</p>

            <Heading>📅 Tab Bulanan</Heading>
            <ul>
              <li>3 summary cards: total pendapatan, total booking, total tamu sepanjang tahun dipilih</li>
              <li>Bar chart pendapatan per bulan</li>
              <li>Tabel breakdown per bulan: Booking | Tamu | Pendapatan | DP Terbayar | Sisa Tagihan</li>
            </ul>

            <Heading>📆 Tab Tahunan</Heading>
            <ul><li>Line chart trend + tabel per tahun</li></ul>

            <Heading>📈 Tab Forecast</Heading>
            <ul>
              <li><strong>Confirmed Revenue</strong> — dari booking Paid/Confirmed di bulan depan</li>
              <li><strong>Projected Total</strong> = <Code>max(confirmed, avg_6_bulan_terakhir)</Code></li>
              <li><strong>Historical Avg</strong> — rata-rata bulanan historis</li>
              <li>Dropdown periode: 3 / 6 / 12 bulan</li>
            </ul>
          </Section>

          <Section id="rooms" icon={<BedDouble />} title="6. Kamar — Kelola Bungalow & Foto">
            <p>URL: <Code>/rooms</Code></p>

            <Heading>Upload Foto</Heading>
            <ol>
              <li>Klik <Btn><ImageIcon className="h-3.5 w-3.5 inline mr-1" />Foto</Btn> pada kartu kamar</li>
              <li>Klik slot "Upload" (dashed border) → pilih file</li>
              <li>Format: JPG / PNG / WEBP, max <strong>5MB</strong></li>
              <li>Maksimal <strong>5 foto per kamar</strong></li>
              <li>Hover foto → klik × untuk hapus (owner only)</li>
            </ol>

            <Heading>iCal Feed URL (Export ke Booking.com/Airbnb)</Heading>
            <ol>
              <li>Klik icon <LinkIcon className="h-4 w-4 inline text-[#D16B4D]" /> di kartu kamar → URL ter-copy ke clipboard</li>
              <li>Paste di dashboard OTA Anda untuk sinkronisasi dua arah</li>
              <li>Format: <Code className="text-xs">/api/ical/room/&lt;room_id&gt;.ics</Code></li>
            </ol>

            <InfoBox tone="success">
              <strong>Strategi anti double-booking:</strong><br />
              • Import iCal dari tiap platform (Bookings → Import iCal)<br />
              • Export iCal Anda ke tiap platform (Rooms → copy URL)<br />
              • Lakukan untuk <em>setiap kamar × setiap platform</em>
            </InfoBox>

            <Heading>Base Rate (harga dasar per malam)</Heading>
            <p>Owner edit via tombol ✏️ pada kartu kamar. Harga ini akan dikalikan multiplier season rule saat auto-calculate harga booking.</p>
          </Section>

          <Section id="settings" icon={<SettingsIcon />} title="7. Settings (Owner Only)" ownerNote>
            <p>URL: <Code>/settings</Code></p>

            <Heading>🏝 Tab Umum</Heading>
            <p>Info homestay yang muncul di <strong>Invoice</strong>: Nama, Alamat, Telepon, Email, Info Rekening.</p>

            <Heading><Webhook className="h-4 w-4 inline" /> Automation — Reminder H-1 via Webhook</Heading>
            <p>Isi <strong>Webhook URL</strong> dari n8n / Make / Zapier. Klik <Btn>Test Webhook</Btn> untuk kirim reminder besok sekarang.</p>
            <Heading>Setup n8n (recommended)</Heading>
            <ol>
              <li>Buat workflow: <strong>Cron Trigger</strong> (daily 09:00) → <strong>HTTP Request</strong> → <strong>Loop</strong> → <strong>WhatsApp/Telegram node</strong></li>
              <li>HTTP Request: <Code>GET ${"{BACKEND_URL}"}/api/reminders/upcoming?days=1</Code></li>
              <li>Header: <Code>Authorization: Bearer &lt;owner_token&gt;</Code></li>
              <li>Loop: kirim ke <Code>{"{{ $json.phone }}"}</Code> dengan pesan <Code>{"{{ $json.message }}"}</Code></li>
            </ol>

            <Heading><Users className="h-4 w-4 inline" /> Tab Users</Heading>
            <ul>
              <li><Btn>Tambah User</Btn>: nama, email, password, role (staff/owner)</li>
              <li>Tidak bisa hapus akun sendiri</li>
            </ul>

            <Heading><DollarSign className="h-4 w-4 inline" /> Tab Dynamic Pricing</Heading>
            <ul>
              <li>Default: <strong>High Season (Des-Feb) × 1.30</strong> (+30%)</li>
              <li>Tambah rule: Nama + Bulan Mulai + Bulan Akhir + Multiplier</li>
              <li>Contoh: <em>Low Season Apr-Jun × 0.85</em> untuk diskon 15%</li>
              <li>Wrap-around bulan support (Des→Feb = Dec+Jan+Feb)</li>
              <li>Jika multiple rules match → multiplier tertinggi dipakai</li>
            </ul>
            <InfoBox tone="warning">
              <strong>Formula harga:</strong><br />
              <Code>nightly_rate = base_rate × max(matching_rules.multiplier)</Code><br />
              <Code>subtotal = jumlah per malam dari check_in sampai check_out − 1</Code>
            </InfoBox>
          </Section>

          <Section id="invoice" icon={<FileText />} title="8. Invoice / Kwitansi">
            <p>URL: <Code>/invoice/&lt;booking_id&gt;</Code> (dibuka via icon 📄 di Bookings)</p>

            <Heading>Struktur Invoice</Heading>
            <ul>
              <li>Header: logo + nama homestay + kontak (dari Settings)</li>
              <li>Nomor: <Code>INV-YYYYMMDD-XXXXXX</Code></li>
              <li>Billed To: nama tamu + nomor WA + platform</li>
              <li>Stay Details: check-in, check-out, malam, jumlah tamu</li>
              <li>Line Item: kamar × qty × harga/malam = total</li>
              <li>Totals: Subtotal − DP = Sisa Tagihan</li>
              <li>Payment Info (dari Settings) + Catatan booking</li>
              <li>Foto kamar (jika ada)</li>
            </ul>

            <Heading><Printer className="h-4 w-4 inline" /> Print / Save as PDF</Heading>
            <ul>
              <li>Klik tombol <Btn>Print / Save as PDF</Btn> di kanan atas</li>
              <li>Atau <Code>Ctrl+P</Code> / <Code>Cmd+P</Code></li>
              <li>Toolbar & sidebar otomatis disembunyikan saat print</li>
            </ul>
          </Section>

          <Section id="tips" icon={<Lightbulb />} title="9. Tips & Best Practices">
            <Heading>✅ Alur Kerja Harian</Heading>
            <ol>
              <li>Cek <strong>Dashboard</strong> pagi hari untuk overview</li>
              <li>Buka <strong>Bookings</strong> → filter status = Pending → follow up</li>
              <li>Export CSV tiap minggu untuk backup</li>
              <li>Update <strong>Calendar</strong> otomatis — tidak perlu manual</li>
            </ol>

            <Heading>✅ Sinkronisasi Multi-Platform</Heading>
            <ol>
              <li>Pasang iCal dua arah tiap minggu (export + import)</li>
              <li>Platform Direct WhatsApp tetap input manual</li>
              <li>Selalu isi nomor WA tamu → aktifkan fitur reminder & konfirmasi</li>
            </ol>

            <Heading>✅ Dynamic Pricing Strategy</Heading>
            <ol>
              <li>Base rate realistis (survei tarif kompetitor Raja Ampat)</li>
              <li>High season (Jun-Sep = peak diving season) = +30–50%</li>
              <li>Low season = diskon 10–15% untuk isi kamar kosong</li>
            </ol>

            <Heading>✅ Keamanan</Heading>
            <ol>
              <li>Login pakai akun sendiri, JANGAN share password</li>
              <li>Staff hanya akses yang dibutuhkan (Reports/Settings diblokir)</li>
              <li>Ganti password owner setelah deploy production</li>
            </ol>
          </Section>

          <Section id="troubleshooting" icon={<AlertTriangle />} title="10. Troubleshooting">
            <div className="rounded-xl border border-[#EBE6DE] overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FDFBF7]">
                  <tr>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#586D61]">Masalah</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-[#586D61]">Solusi</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Login gagal dengan password benar", "Cek caps lock, atau restart backend supaya OWNER_PASSWORD di .env di-apply"],
                    ["Foto tidak muncul", "Cek ukuran < 5MB, format JPG/PNG/WEBP, dan EMERGENT_LLM_KEY di .env"],
                    ["iCal import kosong", "Cek URL valid (test di browser), booking OTA sudah masuk ke calendar"],
                    ["Webhook test gagal", "Cek URL webhook (publicly accessible, support POST JSON)"],
                    ["Total harga auto tidak muncul", "Pastikan base_rate kamar > 0 (edit di Rooms page)"],
                    ["401 Unauthorized tiba-tiba", "Token expired (8 jam) → login ulang otomatis"],
                  ].map(([a, b]) => (
                    <tr key={a} className="border-t border-[#EBE6DE]">
                      <td className="px-4 py-3 font-medium text-[#172E21]">{a}</td>
                      <td className="px-4 py-3 text-[#586D61]">{b}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section id="api" icon={<Code2 />} title="11. API Endpoints (Integrasi)">
            <p>Semua endpoint butuh header <Code>Authorization: Bearer &lt;token&gt;</Code> kecuali:</p>
            <ul>
              <li><Code>POST /api/auth/login</Code> — untuk dapat token</li>
              <li><Code>GET /api/ical/room/&#123;id&#125;.ics</Code> — public (OTA sync)</li>
              <li><Code>GET /api/photos/&#123;room_id&#125;/&#123;photo_id&#125;?auth=&lt;token&gt;</Code> — JWT via query</li>
            </ul>

            <Heading>Highlight endpoint developer</Heading>
            <div className="bg-[#082618] rounded-xl p-4 text-[#FDFBF7] font-mono text-xs overflow-x-auto">
              <div>GET  /api/dashboard/stats?year=&amp;month=</div>
              <div>GET  /api/calendar?year=&amp;month=</div>
              <div>GET  /api/reports/monthly?year=       <span className="text-[#D99338]">(owner)</span></div>
              <div>GET  /api/forecast?months_ahead=      <span className="text-[#D99338]">(owner)</span></div>
              <div>GET  /api/reminders/upcoming?days=1   <span className="text-[#2C7A51]"># untuk n8n cron</span></div>
              <div>GET  /api/pricing/calculate?room_name=&amp;check_in=&amp;check_out=</div>
              <div>GET  /api/ical/room/&#123;id&#125;.ics           <span className="text-[#D99338]">(public)</span></div>
              <div>POST /api/ical/import                 <span className="text-[#D99338]">(owner)</span></div>
              <div>GET  /api/export/csv</div>
              <div>GET  /api/invoices/&#123;booking_id&#125;</div>
            </div>
            <InfoBox>
              Butuh dokumentasi API lengkap (OpenAPI/Swagger)? Buka <Code>{window.location.origin}/docs</Code> untuk interactive API docs bawaan FastAPI (perlu akses langsung backend, bukan via ingress).
            </InfoBox>
          </Section>

          {/* Footer */}
          <div className="mt-8 p-6 rounded-2xl bg-gradient-to-br from-[#082618] to-[#0D3322] text-[#FDFBF7] flex items-center gap-4">
            <div className="flex-1">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#D16B4D]">Butuh Bantuan?</div>
              <div className="font-heading text-xl mt-1">Hubungi admin sistem</div>
              <div className="text-sm text-[#FDFBF7]/70 mt-1">
                Selamat mengelola homestay! 🌴🏝 — Meos Ambower Homestay, Raja Ampat.
              </div>
            </div>
            {isOwner && (
              <div className="hidden md:block text-xs text-[#FDFBF7]/60 text-right">
                Login sebagai <strong className="text-[#FDFBF7]">Owner</strong> — akses penuh
              </div>
            )}
          </div>

        </article>
      </div>
    </>
  );
}

// ---------- Sub-components ----------
function Section({ id, icon, title, children, ownerNote }) {
  return (
    <section id={id} data-testid={`help-section-${id}`} className="bg-white border border-[#EBE6DE] rounded-2xl p-8 shadow-sm scroll-mt-8">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-10 w-10 rounded-xl bg-[#D16B4D]/15 text-[#D16B4D] flex items-center justify-center flex-shrink-0">
          {icon}
        </div>
        <h2 className="font-heading text-2xl font-medium text-[#172E21] flex-1">{title}</h2>
        {ownerNote && (
          <span className="bg-[#D16B4D]/10 text-[#D16B4D] px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider">Owner Only</span>
        )}
      </div>
      <div className="flex flex-col gap-3 text-[15px] text-[#172E21] leading-relaxed [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:flex [&>ul]:flex-col [&>ul]:gap-1.5 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:flex [&>ol]:flex-col [&>ol]:gap-1.5 [&>p]:text-[#586D61]">
        {children}
      </div>
    </section>
  );
}

function Heading({ children }) {
  return (
    <h3 className="font-heading text-base font-semibold text-[#172E21] mt-4 flex items-center gap-2">
      <ChevronRight className="h-4 w-4 text-[#D16B4D]" />
      {children}
    </h3>
  );
}

function Code({ children, className = "" }) {
  return (
    <code className={`bg-[#FDFBF7] text-[#D16B4D] px-1.5 py-0.5 rounded-md font-mono text-[13px] border border-[#EBE6DE] ${className}`}>
      {children}
    </code>
  );
}

function Btn({ children }) {
  return (
    <span className="inline-flex items-center gap-1 bg-[#172E21] text-white px-2.5 py-1 rounded-lg text-xs font-medium">
      {children}
    </span>
  );
}

function Badge({ children, color = "#D16B4D" }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold" style={{ backgroundColor: `${color}18`, color }}>
      {children}
    </span>
  );
}

function InfoBox({ children, tone = "info" }) {
  const styles = {
    info: "bg-[#FDFBF7] border-[#EBE6DE] text-[#586D61]",
    warning: "bg-[#D99338]/8 border-[#D99338]/30 text-[#586D61]",
    success: "bg-[#2C7A51]/8 border-[#2C7A51]/30 text-[#586D61]",
  }[tone];
  return (
    <div className={`rounded-xl border p-4 text-sm leading-relaxed ${styles}`}>
      {children}
    </div>
  );
}
